import { DatabaseBlockDataSource } from '@blocksuite/affine/blocks/database';
import type { DatabaseBlockModel } from '@blocksuite/affine/model';
import { nanoid, Text } from '@blocksuite/affine/store';
// eslint-disable-next-line import-x/no-extraneous-dependencies -- transitive dep from @blocksuite/affine
import { propertyPresets } from '@blocksuite/data-view/property-presets';

import type { DocsService } from '../../doc';

/**
 * Multilingual template support (EN/ES/PT)
 * Default: Spanish (for Latin American community)
 *
 * Language is resolved from the user's AFFiNE language preference
 * (Settings > Appearance > Language), NOT from navigator.language.
 */
type Lang = 'en' | 'es' | 'pt';

/**
 * Maps an AFFiNE Language key (e.g. 'es-CL', 'pt-BR', 'en') to a template Lang.
 * Defaults to Spanish when no preference is set or for unsupported languages.
 */
export function resolveTemplateLang(languageKey?: string): Lang {
  if (!languageKey) return 'es';
  const prefix = languageKey.slice(0, 2).toLowerCase();
  if (prefix === 'en') return 'en';
  if (prefix === 'pt') return 'pt';
  return 'es'; // Default to Spanish
}

let _currentLang: Lang = 'es';

export function setTemplateLang(lang: Lang) {
  _currentLang = lang;
}

const t = {
  // Common
  status: { en: 'Status', es: 'Estado', pt: 'Status' },
  date: { en: 'Date', es: 'Fecha', pt: 'Data' },
  category: { en: 'Category', es: 'Categoría', pt: 'Categoria' },
  priority: { en: 'Priority', es: 'Prioridad', pt: 'Prioridade' },
  assignee: { en: 'Assignee', es: 'Responsable', pt: 'Responsável' },
  owner: { en: 'Owner', es: 'Propietario', pt: 'Proprietário' },
  description: { en: 'Description', es: 'Descripción', pt: 'Descrição' },
  notes: { en: 'Notes', es: 'Notas', pt: 'Notas' },
  type: { en: 'Type', es: 'Tipo', pt: 'Tipo' },

  // Status options
  toDo: { en: 'To Do', es: 'Por Hacer', pt: 'A Fazer' },
  inProgress: { en: 'In Progress', es: 'En Progreso', pt: 'Em Progresso' },
  review: { en: 'Review', es: 'Revisión', pt: 'Revisão' },
  done: { en: 'Done', es: 'Completado', pt: 'Concluído' },
  open: { en: 'Open', es: 'Abierto', pt: 'Aberto' },
  closed: { en: 'Closed', es: 'Cerrado', pt: 'Fechado' },
  blocked: { en: 'Blocked', es: 'Bloqueado', pt: 'Bloqueado' },
  notStarted: { en: 'Not Started', es: 'No Iniciado', pt: 'Não Iniciado' },
  onTrack: { en: 'On Track', es: 'En Curso', pt: 'No Prazo' },
  atRisk: { en: 'At Risk', es: 'En Riesgo', pt: 'Em Risco' },
  completed: { en: 'Completed', es: 'Completado', pt: 'Concluído' },

  // Priority options
  high: { en: 'High', es: 'Alta', pt: 'Alta' },
  medium: { en: 'Medium', es: 'Media', pt: 'Média' },
  low: { en: 'Low', es: 'Low', pt: 'Baixa' },
  critical: { en: 'Critical', es: 'Crítica', pt: 'Crítica' },

  // Sprint Planning
  sprintPlanning: {
    en: 'Sprint Planning',
    es: 'Planificación de Sprint',
    pt: 'Planejamento de Sprint',
  },
  sprintGoals: {
    en: 'Sprint Goals',
    es: 'Objetivos del Sprint',
    pt: 'Objetivos do Sprint',
  },
  sprintBacklog: {
    en: 'Sprint Backlog',
    es: 'Backlog del Sprint',
    pt: 'Backlog do Sprint',
  },
  sprintTasks: {
    en: 'Sprint Tasks',
    es: 'Tareas del Sprint',
    pt: 'Tarefas do Sprint',
  },
  points: { en: 'Points', es: 'Puntos', pt: 'Pontos' },

  // 1:1 Meeting
  oneOnOne: { en: '1:1 Meeting', es: 'Reunión 1:1', pt: 'Reunião 1:1' },
  meetingWith: { en: 'Meeting with:', es: 'Reunión con:', pt: 'Reunião com:' },
  agenda: { en: 'Agenda', es: 'Agenda', pt: 'Agenda' },
  discussionPoints: {
    en: 'Discussion Points',
    es: 'Puntos de Discusión',
    pt: 'Pontos de Discussão',
  },
  actionItems: {
    en: 'Action Items',
    es: 'Acciones a Tomar',
    pt: 'Itens de Ação',
  },
  followUp: { en: 'Follow-up', es: 'Seguimiento', pt: 'Acompanhamento' },
  nextMeeting: {
    en: 'Next Meeting:',
    es: 'Próxima Reunión:',
    pt: 'Próxima Reunião:',
  },

  // Weekly Standup
  weeklyStandup: {
    en: 'Weekly Standup',
    es: 'Standup Semanal',
    pt: 'Standup Semanal',
  },
  teamUpdates: {
    en: 'Team Updates',
    es: 'Actualizaciones del Equipo',
    pt: 'Atualizações da Equipe',
  },
  teamMember: {
    en: 'Team Member',
    es: 'Miembro del Equipo',
    pt: 'Membro da Equipe',
  },
  lastWeek: { en: 'Last Week', es: 'Semana Pasada', pt: 'Semana Passada' },
  thisWeek: { en: 'This Week', es: 'Esta Semana', pt: 'Esta Semana' },
  blockers: { en: 'Blockers', es: 'Bloqueadores', pt: 'Bloqueios' },
  announcements: { en: 'Announcements', es: 'Anuncios', pt: 'Anúncios' },

  // Project Tracker
  projectTracker: {
    en: 'Project Tracker',
    es: 'Seguimiento de Proyectos',
    pt: 'Rastreador de Projetos',
  },
  projects: { en: 'Projects', es: 'Proyectos', pt: 'Projetos' },
  projectName: {
    en: 'Project Name',
    es: 'Nombre del Proyecto',
    pt: 'Nome do Projeto',
  },
  startDate: { en: 'Start Date', es: 'Fecha de Inicio', pt: 'Data de Início' },
  dueDate: { en: 'Due Date', es: 'Fecha Límite', pt: 'Data de Entrega' },
  lead: { en: 'Lead', es: 'Líder', pt: 'Líder' },

  // Retrospective
  retrospective: {
    en: 'Retrospective',
    es: 'Retrospectiva',
    pt: 'Retrospectiva',
  },
  retro: { en: 'Retrospective', es: 'Retrospectiva', pt: 'Retrospectiva' },
  wentWell: { en: 'What Went Well', es: 'Qué Salió Bien', pt: 'O Que Foi Bem' },
  improve: {
    en: 'What Could Improve',
    es: 'Qué Podría Mejorar',
    pt: 'O Que Pode Melhorar',
  },
  tryNext: {
    en: 'What to Try Next',
    es: 'Qué Probar a Continuación',
    pt: 'O Que Tentar a Seguir',
  },
  sprintHighlights: {
    en: 'Sprint Highlights',
    es: 'Destacados del Sprint',
    pt: 'Destaques do Sprint',
  },

  // Bug Report
  bugReport: { en: 'Bug Report', es: 'Reporte de Bug', pt: 'Relatório de Bug' },
  bugTitle: { en: 'Bug Title', es: 'Título del Bug', pt: 'Título do Bug' },
  stepsToReproduce: {
    en: 'Steps to Reproduce',
    es: 'Pasos para Reproducir',
    pt: 'Passos para Reproduzir',
  },
  expectedBehavior: {
    en: 'Expected Behavior',
    es: 'Comportamiento Esperado',
    pt: 'Comportamento Esperado',
  },
  actualBehavior: {
    en: 'Actual Behavior',
    es: 'Comportamiento Actual',
    pt: 'Comportamento Real',
  },
  environment: { en: 'Environment', es: 'Entorno', pt: 'Ambiente' },
  browser: { en: 'Browser', es: 'Navegador', pt: 'Navegador' },
  os: { en: 'OS', es: 'Sistema Operativo', pt: 'Sistema Operacional' },
  version: { en: 'Version', es: 'Versión', pt: 'Versão' },
  severity: { en: 'Severity', es: 'Severidad', pt: 'Severidade' },
  reportedBy: { en: 'Reported By', es: 'Reportado Por', pt: 'Reportado Por' },
  additionalContext: {
    en: 'Additional Context',
    es: 'Contexto Adicional',
    pt: 'Contexto Adicional',
  },

  // Feature Request
  featureRequest: {
    en: 'Feature Request',
    es: 'Solicitud de Funcionalidad',
    pt: 'Solicitação de Recurso',
  },
  featureTitle: {
    en: 'Feature Title',
    es: 'Título de la Funcionalidad',
    pt: 'Título do Recurso',
  },
  problemStatement: {
    en: 'Problem Statement',
    es: 'Declaración del Problema',
    pt: 'Declaração do Problema',
  },
  proposedSolution: {
    en: 'Proposed Solution',
    es: 'Solución Propuesta',
    pt: 'Solução Proposta',
  },
  alternatives: {
    en: 'Alternatives Considered',
    es: 'Alternativas Consideradas',
    pt: 'Alternativas Consideradas',
  },
  acceptanceCriteria: {
    en: 'Acceptance Criteria',
    es: 'Criterios de Aceptación',
    pt: 'Critérios de Aceitação',
  },
  requestedBy: {
    en: 'Requested By',
    es: 'Solicitado Por',
    pt: 'Solicitado Por',
  },
  impact: { en: 'Impact', es: 'Impacto', pt: 'Impacto' },

  // Team Wiki
  teamWiki: { en: 'Team Wiki', es: 'Wiki del Equipo', pt: 'Wiki da Equipe' },
  welcome: { en: 'Welcome', es: 'Bienvenido', pt: 'Bem-vindo' },
  welcomeDesc: {
    en: 'Welcome to the team wiki! This is a central place for team documentation.',
    es: '¡Bienvenido a la wiki del equipo! Este es un lugar central para la documentación del equipo.',
    pt: 'Bem-vindo à wiki da equipe! Este é um lugar central para a documentação da equipe.',
  },
  quickLinks: { en: 'Quick Links', es: 'Enlaces Rápidos', pt: 'Links Rápidos' },
  onboarding: {
    en: 'Onboarding Guide',
    es: 'Guía de Incorporación',
    pt: 'Guia de Integração',
  },
  codeStandards: {
    en: 'Code Standards',
    es: 'Estándares de Código',
    pt: 'Padrões de Código',
  },
  devSetup: {
    en: 'Development Setup',
    es: 'Configuración de Desarrollo',
    pt: 'Configuração de Desenvolvimento',
  },
  releaseProcess: {
    en: 'Release Process',
    es: 'Proceso de Release',
    pt: 'Processo de Release',
  },
  teamNorms: {
    en: 'Team Norms',
    es: 'Normas del Equipo',
    pt: 'Normas da Equipe',
  },
  contacts: {
    en: 'Team Contacts',
    es: 'Contactos del Equipo',
    pt: 'Contatos da Equipe',
  },

  // Decision Log
  decisionLog: {
    en: 'Decision Log',
    es: 'Registro de Decisiones',
    pt: 'Registro de Decisões',
  },
  decisions: { en: 'Decisions', es: 'Decisiones', pt: 'Decisões' },
  proposed: { en: 'Proposed', es: 'Propuesto', pt: 'Proposto' },
  approved: { en: 'Approved', es: 'Aprobado', pt: 'Aprovado' },
  rejected: { en: 'Rejected', es: 'Rechazado', pt: 'Rejeitado' },
  superseded: { en: 'Superseded', es: 'Reemplazado', pt: 'Substituído' },
  architecture: { en: 'Architecture', es: 'Arquitectura', pt: 'Arquitetura' },
  process: { en: 'Process', es: 'Proceso', pt: 'Processo' },
  tooling: { en: 'Tooling', es: 'Herramientas', pt: 'Ferramentas' },
  team: { en: 'Team', es: 'Equipo', pt: 'Equipe' },
  decisionMaker: {
    en: 'Decision Maker',
    es: 'Tomador de Decisiones',
    pt: 'Tomador de Decisões',
  },

  // Daily Journal
  dailyJournal: {
    en: 'Daily Journal',
    es: 'Diario Personal',
    pt: 'Diário Pessoal',
  },
  morningIntention: {
    en: 'Morning Intention',
    es: 'Intención de la Mañana',
    pt: 'Intenção da Manhã',
  },
  mainFocus: {
    en: "What's my main focus for today?",
    es: '¿Cuál es mi enfoque principal para hoy?',
    pt: 'Qual é meu foco principal para hoje?',
  },
  todaysPriorities: {
    en: "Today's Priorities",
    es: 'Prioridades de Hoy',
    pt: 'Prioridades de Hoje',
  },
  gratitude: { en: 'Gratitude', es: 'Gratitud', pt: 'Gratidão' },
  gratitudePrompt: {
    en: 'Three things I am grateful for today:',
    es: 'Tres cosas por las que estoy agradecido hoy:',
    pt: 'Três coisas pelas quais sou grato hoje:',
  },
  notesReflections: {
    en: 'Notes & Reflections',
    es: 'Notas y Reflexiones',
    pt: 'Notas e Reflexões',
  },
  endOfDayReview: {
    en: 'End of Day Review',
    es: 'Revisión del Día',
    pt: 'Revisão do Dia',
  },
  whatWentWell: {
    en: 'What went well?',
    es: '¿Qué salió bien?',
    pt: 'O que foi bem?',
  },
  whatCouldImprove: {
    en: 'What could be improved?',
    es: '¿Qué podría mejorar?',
    pt: 'O que pode melhorar?',
  },
  whatDidILearn: {
    en: 'What did I learn?',
    es: '¿Qué aprendí?',
    pt: 'O que aprendi?',
  },

  // Action Items table columns
  task: { en: 'Task', es: 'Tarea', pt: 'Tarefa' },
  responsible: { en: 'Responsible', es: 'Responsable', pt: 'Responsável' },

  // 1:1 Meeting additional
  careerDevelopment: {
    en: 'Career Development',
    es: 'Desarrollo Profesional',
    pt: 'Desenvolvimento de Carreira',
  },
  careerPrompt: {
    en: 'Goals, growth, learning opportunities',
    es: 'Metas, crecimiento, oportunidades de aprendizaje',
    pt: 'Metas, crescimento, oportunidades de aprendizado',
  },

  // Weekly Standup prompts
  accomplishPrompt: {
    en: 'What did you accomplish?',
    es: '¿Qué lograste?',
    pt: 'O que você realizou?',
  },
  workingOnPrompt: {
    en: 'What are you working on?',
    es: '¿En qué estás trabajando?',
    pt: 'No que você está trabalhando?',
  },
  blockersPrompt: {
    en: 'Anything blocking your progress?',
    es: '¿Algo está bloqueando tu progreso?',
    pt: 'Algo está bloqueando seu progresso?',
  },
  highlights: { en: 'Highlights', es: 'Destacados', pt: 'Destaques' },
  highlightsPrompt: {
    en: 'Wins, learnings, shoutouts',
    es: 'Logros, aprendizajes, reconocimientos',
    pt: 'Vitórias, aprendizados, reconhecimentos',
  },

  // Project Tracker additional
  projectOverview: {
    en: 'Project Overview',
    es: 'Resumen del Proyecto',
    pt: 'Visão Geral do Projeto',
  },
  projectOverviewPrompt: {
    en: 'Brief description of the project goals and scope.',
    es: 'Breve descripción de los objetivos y alcance del proyecto.',
    pt: 'Breve descrição dos objetivos e escopo do projeto.',
  },
  milestones: { en: 'Milestones', es: 'Hitos', pt: 'Marcos' },

  // Bug Report prompts
  stepsPrompt: {
    en: '1. Step one\n2. Step two\n3. ...',
    es: '1. Paso uno\n2. Paso dos\n3. ...',
    pt: '1. Passo um\n2. Passo dois\n3. ...',
  },
  expectedPrompt: {
    en: 'What should happen?',
    es: '¿Qué debería ocurrir?',
    pt: 'O que deveria acontecer?',
  },
  actualPrompt: {
    en: 'What actually happens?',
    es: '¿Qué ocurre realmente?',
    pt: 'O que realmente acontece?',
  },
  screenshotsLogs: {
    en: 'Screenshots / Logs',
    es: 'Capturas de Pantalla / Registros',
    pt: 'Capturas de Tela / Logs',
  },
  addScreenshots: {
    en: 'Add any relevant screenshots or error logs here.',
    es: 'Agregue capturas de pantalla o registros de errores relevantes aquí.',
    pt: 'Adicione capturas de tela ou logs de erro relevantes aqui.',
  },

  // Feature Request prompts
  problemPrompt: {
    en: 'What problem does this feature solve?',
    es: '¿Qué problema resuelve esta funcionalidad?',
    pt: 'Que problema esse recurso resolve?',
  },
  solutionPrompt: {
    en: 'How should this feature work?',
    es: '¿Cómo debería funcionar esta funcionalidad?',
    pt: 'Como esse recurso deve funcionar?',
  },
  alternativesPrompt: {
    en: 'What alternatives have you considered?',
    es: '¿Qué alternativas has considerado?',
    pt: 'Que alternativas você considerou?',
  },
  criteriaPrompt: {
    en: 'How will we know this feature is successful?',
    es: '¿Cómo sabremos que esta funcionalidad es exitosa?',
    pt: 'Como saberemos que esse recurso é bem-sucedido?',
  },

  // Retrospective additional
  retroItems: {
    en: 'Retrospective Items',
    es: 'Elementos de Retrospectiva',
    pt: 'Itens de Retrospectiva',
  },
  sentiment: { en: 'Sentiment', es: 'Sentimiento', pt: 'Sentimento' },
  positive: { en: 'Positive', es: 'Positivo', pt: 'Positivo' },
  negative: { en: 'Negative', es: 'Negativo', pt: 'Negativo' },
  neutral: { en: 'Neutral', es: 'Neutral', pt: 'Neutro' },
  celebrateWins: {
    en: 'Celebrate successes and wins',
    es: 'Celebra los éxitos y logros',
    pt: 'Celebre os sucessos e vitórias',
  },
  areasImprove: {
    en: 'Areas for improvement',
    es: 'Áreas de mejora',
    pt: 'Áreas para melhorar',
  },
  concreteSteps: {
    en: 'Concrete steps to take',
    es: 'Pasos concretos a tomar',
    pt: 'Passos concretos a tomar',
  },
  shoutouts: { en: 'Shoutouts', es: 'Reconocimientos', pt: 'Reconhecimentos' },
  recognizeTeam: {
    en: 'Recognize team members',
    es: 'Reconoce a los miembros del equipo',
    pt: 'Reconheça os membros da equipe',
  },

  // Bug Report additional
  summary: { en: 'Summary', es: 'Resumen', pt: 'Resumo' },
  briefBugDesc: {
    en: 'Brief description of the bug',
    es: 'Breve descripción del bug',
    pt: 'Breve descrição do bug',
  },
  step1: { en: 'Step 1', es: 'Paso 1', pt: 'Passo 1' },
  whatShouldHappen: {
    en: 'What should happen',
    es: 'Qué debería ocurrir',
    pt: 'O que deveria acontecer',
  },
  whatActuallyHappens: {
    en: 'What actually happens',
    es: 'Qué ocurre realmente',
    pt: 'O que realmente acontece',
  },
  browserOs: { en: 'Browser/OS:', es: 'Navegador/SO:', pt: 'Navegador/SO:' },
  versionLabel: { en: 'Version:', es: 'Versión:', pt: 'Versão:' },
  attachScreenshots: {
    en: 'Attach relevant screenshots or error logs',
    es: 'Adjunte capturas de pantalla o registros de errores relevantes',
    pt: 'Anexe capturas de tela ou logs de erro relevantes',
  },
  possibleFix: {
    en: 'Possible Fix',
    es: 'Posible Solución',
    pt: 'Possível Solução',
  },
  suggestionsFixing: {
    en: 'Optional: suggestions for fixing',
    es: 'Opcional: sugerencias para solucionar',
    pt: 'Opcional: sugestões para correção',
  },

  // Feature Request additional
  featureOverview: {
    en: 'Feature Overview',
    es: 'Resumen de la Funcionalidad',
    pt: 'Visão Geral do Recurso',
  },
  describeFeature: {
    en: 'Describe the feature you would like',
    es: 'Describe la funcionalidad que deseas',
    pt: 'Descreva o recurso que você deseja',
  },
  useCases: { en: 'Use Cases', es: 'Casos de Uso', pt: 'Casos de Uso' },
  whoWillUse: {
    en: 'Who will use this and how?',
    es: '¿Quién usará esto y cómo?',
    pt: 'Quem usará isso e como?',
  },
  benefits: { en: 'Benefits', es: 'Beneficios', pt: 'Benefícios' },
  whatBenefits: {
    en: 'What are the expected benefits?',
    es: '¿Cuáles son los beneficios esperados?',
    pt: 'Quais são os benefícios esperados?',
  },
  additionalNotes: {
    en: 'Additional Notes',
    es: 'Notas Adicionales',
    pt: 'Notas Adicionais',
  },

  // Team Wiki additional
  gettingStarted: {
    en: 'Getting Started',
    es: 'Primeros Pasos',
    pt: 'Primeiros Passos',
  },
  resourcesLinks: {
    en: 'Resources & Links',
    es: 'Recursos y Enlaces',
    pt: 'Recursos e Links',
  },
  teamDirectory: {
    en: 'Team Directory',
    es: 'Directorio del Equipo',
    pt: 'Diretório da Equipe',
  },
  faq: { en: 'FAQ', es: 'Preguntas Frecuentes', pt: 'Perguntas Frequentes' },
  aboutTeam: {
    en: 'About the Team',
    es: 'Sobre el Equipo',
    pt: 'Sobre a Equipe',
  },
  teamMission: {
    en: 'Team mission, goals, and values',
    es: 'Misión, objetivos y valores del equipo',
    pt: 'Missão, objetivos e valores da equipe',
  },
  teamMembers: {
    en: 'Team Members',
    es: 'Miembros del Equipo',
    pt: 'Membros da Equipe',
  },
  nameRoleContact: {
    en: 'Name - Role - Contact',
    es: 'Nombre - Rol - Contacto',
    pt: 'Nome - Função - Contato',
  },
  communication: { en: 'Communication', es: 'Comunicación', pt: 'Comunicação' },
  slackChannels: {
    en: 'Slack channels',
    es: 'Canales de Slack',
    pt: 'Canais do Slack',
  },
  meetingSchedule: {
    en: 'Meeting schedule',
    es: 'Horario de reuniones',
    pt: 'Agenda de reuniões',
  },
  responseTime: {
    en: 'Response time expectations',
    es: 'Expectativas de tiempo de respuesta',
    pt: 'Expectativas de tempo de resposta',
  },
  processes: { en: 'Processes', es: 'Procesos', pt: 'Processos' },
  devWorkflow: {
    en: 'Development Workflow',
    es: 'Flujo de Desarrollo',
    pt: 'Fluxo de Desenvolvimento',
  },
  howWeBuild: {
    en: 'How we build and ship',
    es: 'Cómo construimos y desplegamos',
    pt: 'Como construímos e implantamos',
  },
  codeReview: {
    en: 'Code Review',
    es: 'Revisión de Código',
    pt: 'Revisão de Código',
  },
  codeReviewGuidelines: {
    en: 'Code review guidelines',
    es: 'Guías de revisión de código',
    pt: 'Diretrizes de revisão de código',
  },
  deployments: { en: 'Deployments', es: 'Despliegues', pt: 'Implantações' },
  deploymentProcess: {
    en: 'Deployment process',
    es: 'Proceso de despliegue',
    pt: 'Processo de implantação',
  },
  resources: { en: 'Resources', es: 'Recursos', pt: 'Recursos' },
  importantLinks: {
    en: 'Important links',
    es: 'Enlaces importantes',
    pt: 'Links importantes',
  },
  firstWeekChecklist: {
    en: 'First week checklist',
    es: 'Lista de la primera semana',
    pt: 'Lista da primeira semana',
  },
} as const;

function tr(key: keyof typeof t): string {
  return t[key][_currentLang];
}

/**
 * Helper to add blocks to a note
 */
async function addContentBlocks(
  store: any,
  noteBlockId: string,
  blocks: Array<{
    type: 'heading' | 'paragraph' | 'list' | 'divider';
    level?: number;
    listType?: 'bulleted' | 'numbered' | 'todo';
    text?: string;
  }>
) {
  for (const block of blocks) {
    if (block.type === 'heading') {
      store.addBlock(
        'affine:paragraph',
        { type: `h${block.level || 2}`, text: new Text(block.text || '') },
        noteBlockId
      );
    } else if (block.type === 'paragraph') {
      store.addBlock(
        'affine:paragraph',
        { type: 'text', text: new Text(block.text || '') },
        noteBlockId
      );
    } else if (block.type === 'list') {
      store.addBlock(
        'affine:list',
        {
          type: block.listType || 'bulleted',
          text: new Text(block.text || ''),
        },
        noteBlockId
      );
    } else if (block.type === 'divider') {
      store.addBlock('affine:divider', {}, noteBlockId);
    }
  }
}

/**
 * Sprint Planning Template
 * Database for tracking sprint tasks with status, assignee, points
 */
export async function createSprintPlanningTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('sprintPlanning'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      // Add sprint header
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('sprintGoals') },
        { type: 'list', listType: 'numbered', text: '' },
        { type: 'heading', level: 2, text: tr('sprintBacklog') },
      ]);

      // Create database block
      const databaseId = store.addBlock(
        'affine:database',
        { columns: [], cells: {} },
        noteBlock.id
      );

      const databaseModel = store.getBlock(databaseId)
        ?.model as DatabaseBlockModel;

      if (databaseModel) {
        databaseModel.props.title = new Text(tr('sprintTasks'));
        const datasource = new DatabaseBlockDataSource(databaseModel);
        datasource.viewManager.viewAdd('table');

        // Status column
        const statusId = datasource.propertyAdd('end', {
          type: propertyPresets.selectPropertyConfig.type,
          name: tr('status'),
        });
        if (statusId) {
          datasource.propertyDataSet(statusId, {
            options: [
              {
                id: nanoid(),
                value: tr('toDo'),
                color: 'var(--affine-tag-gray)',
              },
              {
                id: nanoid(),
                value: tr('inProgress'),
                color: 'var(--affine-tag-blue)',
              },
              {
                id: nanoid(),
                value: tr('review'),
                color: 'var(--affine-tag-purple)',
              },
              {
                id: nanoid(),
                value: tr('done'),
                color: 'var(--affine-tag-green)',
              },
            ],
          });
        }

        // Assignee column
        datasource.propertyAdd('end', {
          type: propertyPresets.textPropertyConfig.type,
          name: tr('assignee'),
        });

        // Points column
        datasource.propertyAdd('end', {
          type: propertyPresets.numberPropertyConfig.type,
          name: tr('points'),
        });

        // Priority column
        const priorityId = datasource.propertyAdd('end', {
          type: propertyPresets.selectPropertyConfig.type,
          name: tr('priority'),
        });
        if (priorityId) {
          datasource.propertyDataSet(priorityId, {
            options: [
              {
                id: nanoid(),
                value: tr('high'),
                color: 'var(--affine-tag-red)',
              },
              {
                id: nanoid(),
                value: tr('medium'),
                color: 'var(--affine-tag-orange)',
              },
              {
                id: nanoid(),
                value: tr('low'),
                color: 'var(--affine-tag-green)',
              },
            ],
          });
        }

        // Add kanban view
        datasource.viewManager.viewAdd('kanban');
      }

      // Add notes section
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('notes') },
        { type: 'paragraph', text: '' },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * 1:1 Meeting Template
 * Structured template for one-on-one meetings
 */
export async function createOneOnOneTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('oneOnOne'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('agenda') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('discussionPoints') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('actionItems') },
        { type: 'list', listType: 'todo', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('followUp') },
        { type: 'paragraph', text: tr('nextMeeting') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('careerDevelopment') },
        { type: 'paragraph', text: tr('careerPrompt') },
        { type: 'list', listType: 'bulleted', text: '' },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * Weekly Standup Template
 * Quick status update format
 */
export async function createWeeklyStandupTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('weeklyStandup'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('lastWeek') },
        { type: 'paragraph', text: tr('accomplishPrompt') },
        { type: 'list', listType: 'todo', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('thisWeek') },
        { type: 'paragraph', text: tr('workingOnPrompt') },
        { type: 'list', listType: 'todo', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('blockers') },
        { type: 'paragraph', text: tr('blockersPrompt') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('highlights') },
        { type: 'paragraph', text: tr('highlightsPrompt') },
        { type: 'list', listType: 'bulleted', text: '' },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * Project Tracker Template
 * Database for tracking project milestones and tasks
 */
export async function createProjectTrackerTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('projectTracker'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('projectOverview') },
        { type: 'paragraph', text: tr('projectOverviewPrompt') },
        { type: 'heading', level: 2, text: tr('milestones') },
      ]);

      // Create database block
      const databaseId = store.addBlock(
        'affine:database',
        { columns: [], cells: {} },
        noteBlock.id
      );

      const databaseModel = store.getBlock(databaseId)
        ?.model as DatabaseBlockModel;

      if (databaseModel) {
        databaseModel.props.title = new Text(tr('task'));
        const datasource = new DatabaseBlockDataSource(databaseModel);
        datasource.viewManager.viewAdd('table');

        // Status column
        const statusId = datasource.propertyAdd('end', {
          type: propertyPresets.selectPropertyConfig.type,
          name: tr('status'),
        });
        if (statusId) {
          datasource.propertyDataSet(statusId, {
            options: [
              {
                id: nanoid(),
                value: tr('notStarted'),
                color: 'var(--affine-tag-gray)',
              },
              {
                id: nanoid(),
                value: tr('inProgress'),
                color: 'var(--affine-tag-blue)',
              },
              {
                id: nanoid(),
                value: tr('completed'),
                color: 'var(--affine-tag-green)',
              },
              {
                id: nanoid(),
                value: tr('blocked'),
                color: 'var(--affine-tag-orange)',
              },
            ],
          });
        }

        // Due Date column
        datasource.propertyAdd('end', {
          type: propertyPresets.datePropertyConfig.type,
          name: tr('dueDate'),
        });

        // Owner column
        datasource.propertyAdd('end', {
          type: propertyPresets.textPropertyConfig.type,
          name: tr('owner'),
        });

        // Progress column
        datasource.propertyAdd('end', {
          type: propertyPresets.progressPropertyConfig.type,
          name: tr('status'),
        });

        // Add kanban view
        datasource.viewManager.viewAdd('kanban');
      }

      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('quickLinks') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'heading', level: 2, text: tr('notes') },
        { type: 'paragraph', text: '' },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * Retrospective Template
 * Sprint/project retrospective format
 */
export async function createRetrospectiveTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('retrospective'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('wentWell') },
        { type: 'paragraph', text: tr('celebrateWins') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('improve') },
        { type: 'paragraph', text: tr('areasImprove') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('actionItems') },
        { type: 'paragraph', text: tr('concreteSteps') },
        { type: 'list', listType: 'todo', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('shoutouts') },
        { type: 'paragraph', text: tr('recognizeTeam') },
        { type: 'list', listType: 'bulleted', text: '' },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * Bug Report Template
 * Structured bug report format
 */
export async function createBugReportTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('bugReport'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('summary') },
        { type: 'paragraph', text: tr('briefBugDesc') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('stepsToReproduce') },
        { type: 'list', listType: 'numbered', text: tr('step1') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('expectedBehavior') },
        { type: 'paragraph', text: tr('whatShouldHappen') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('actualBehavior') },
        { type: 'paragraph', text: tr('whatActuallyHappens') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('environment') },
        { type: 'list', listType: 'bulleted', text: tr('browserOs') },
        { type: 'list', listType: 'bulleted', text: tr('versionLabel') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('screenshotsLogs') },
        { type: 'paragraph', text: tr('attachScreenshots') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('possibleFix') },
        { type: 'paragraph', text: tr('suggestionsFixing') },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * Feature Request / PRD Template
 * Product requirements document
 */
export async function createFeatureRequestTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('featureRequest'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('featureOverview') },
        { type: 'paragraph', text: tr('describeFeature') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('problemStatement') },
        { type: 'paragraph', text: tr('problemPrompt') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('proposedSolution') },
        { type: 'paragraph', text: tr('solutionPrompt') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('useCases') },
        { type: 'paragraph', text: tr('whoWillUse') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('acceptanceCriteria') },
        { type: 'paragraph', text: tr('criteriaPrompt') },
        { type: 'list', listType: 'todo', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('benefits') },
        { type: 'paragraph', text: tr('whatBenefits') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('alternatives') },
        { type: 'paragraph', text: tr('alternativesPrompt') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('additionalNotes') },
        { type: 'paragraph', text: '' },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * Team Wiki Template
 * Team handbook/wiki structure
 */
export async function createTeamWikiTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('teamWiki'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('aboutTeam') },
        { type: 'paragraph', text: tr('teamMission') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('teamMembers') },
        { type: 'list', listType: 'bulleted', text: tr('nameRoleContact') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('communication') },
        { type: 'list', listType: 'bulleted', text: tr('slackChannels') },
        { type: 'list', listType: 'bulleted', text: tr('meetingSchedule') },
        { type: 'list', listType: 'bulleted', text: tr('responseTime') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('processes') },
        { type: 'heading', level: 3, text: tr('devWorkflow') },
        { type: 'paragraph', text: tr('howWeBuild') },
        { type: 'heading', level: 3, text: tr('codeReview') },
        { type: 'paragraph', text: tr('codeReviewGuidelines') },
        { type: 'heading', level: 3, text: tr('deployments') },
        { type: 'paragraph', text: tr('deploymentProcess') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('resources') },
        { type: 'list', listType: 'bulleted', text: tr('importantLinks') },
        { type: 'divider' },
        { type: 'heading', level: 2, text: tr('onboarding') },
        { type: 'list', listType: 'todo', text: tr('firstWeekChecklist') },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * Decision Log Template
 * Track important decisions
 */
export async function createDecisionLogTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('decisionLog'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        { type: 'heading', level: 2, text: tr('decisions') },
      ]);

      // Create database block
      const databaseId = store.addBlock(
        'affine:database',
        { columns: [], cells: {} },
        noteBlock.id
      );

      const databaseModel = store.getBlock(databaseId)
        ?.model as DatabaseBlockModel;

      if (databaseModel) {
        databaseModel.props.title = new Text(tr('decisionLog'));
        const datasource = new DatabaseBlockDataSource(databaseModel);
        datasource.viewManager.viewAdd('table');

        // Date column
        datasource.propertyAdd('end', {
          type: propertyPresets.datePropertyConfig.type,
          name: tr('date'),
        });

        // Status column
        const statusId = datasource.propertyAdd('end', {
          type: propertyPresets.selectPropertyConfig.type,
          name: tr('status'),
        });
        if (statusId) {
          datasource.propertyDataSet(statusId, {
            options: [
              {
                id: nanoid(),
                value: tr('proposed'),
                color: 'var(--affine-tag-gray)',
              },
              {
                id: nanoid(),
                value: tr('approved'),
                color: 'var(--affine-tag-green)',
              },
              {
                id: nanoid(),
                value: tr('rejected'),
                color: 'var(--affine-tag-red)',
              },
              {
                id: nanoid(),
                value: tr('superseded'),
                color: 'var(--affine-tag-orange)',
              },
            ],
          });
        }

        // Category column
        const categoryId = datasource.propertyAdd('end', {
          type: propertyPresets.selectPropertyConfig.type,
          name: tr('category'),
        });
        if (categoryId) {
          datasource.propertyDataSet(categoryId, {
            options: [
              {
                id: nanoid(),
                value: tr('architecture'),
                color: 'var(--affine-tag-blue)',
              },
              {
                id: nanoid(),
                value: tr('process'),
                color: 'var(--affine-tag-purple)',
              },
              {
                id: nanoid(),
                value: tr('tooling'),
                color: 'var(--affine-tag-green)',
              },
              {
                id: nanoid(),
                value: tr('team'),
                color: 'var(--affine-tag-orange)',
              },
            ],
          });
        }

        // Owner column
        datasource.propertyAdd('end', {
          type: propertyPresets.textPropertyConfig.type,
          name: tr('decisionMaker'),
        });
      }

      await addContentBlocks(store, noteBlock.id, [
        { type: 'paragraph', text: '' },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

/**
 * Daily Journal Template
 * Personal template for daily reflection and planning
 */
export async function createDailyJournalTemplate(docsService: DocsService) {
  const templateDoc = docsService.createDoc({ isTemplate: true });
  const { doc, release } = docsService.open(templateDoc.id);

  try {
    await doc.waitForSyncReady();
    const store = doc.blockSuiteDoc;
    doc.changeDocTitle(tr('dailyJournal'));

    const noteBlock = store.root?.children.find(
      (child: { flavour: string }) => child.flavour === 'affine:note'
    );

    if (noteBlock) {
      await addContentBlocks(store, noteBlock.id, [
        // Morning Intention
        { type: 'heading', level: 2, text: tr('morningIntention') },
        { type: 'paragraph', text: tr('mainFocus') },
        { type: 'paragraph', text: '' },

        { type: 'divider' },

        // Today's Priorities
        { type: 'heading', level: 2, text: tr('todaysPriorities') },
        { type: 'list', listType: 'todo', text: '' },
        { type: 'list', listType: 'todo', text: '' },
        { type: 'list', listType: 'todo', text: '' },

        { type: 'divider' },

        // Gratitude
        { type: 'heading', level: 2, text: tr('gratitude') },
        { type: 'paragraph', text: tr('gratitudePrompt') },
        { type: 'list', listType: 'numbered', text: '' },
        { type: 'list', listType: 'numbered', text: '' },
        { type: 'list', listType: 'numbered', text: '' },

        { type: 'divider' },

        // Notes & Reflections
        { type: 'heading', level: 2, text: tr('notesReflections') },
        { type: 'paragraph', text: '' },

        { type: 'divider' },

        // End of Day Review
        { type: 'heading', level: 2, text: tr('endOfDayReview') },
        { type: 'heading', level: 3, text: tr('whatWentWell') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'heading', level: 3, text: tr('whatCouldImprove') },
        { type: 'list', listType: 'bulleted', text: '' },
        { type: 'heading', level: 3, text: tr('whatDidILearn') },
        { type: 'list', listType: 'bulleted', text: '' },
      ]);
    }
  } finally {
    release();
  }

  return templateDoc;
}

const BUILT_IN_TEMPLATE_COUNT = 10;

/**
 * Ensures all team templates exist in the workspace.
 *
 * Uses template count rather than title matching to detect existing templates,
 * so that changing the UI language does not duplicate the set.
 */
export async function ensureAllTemplates(
  docsService: DocsService,
  templateList: { getTemplateDocs: () => Array<unknown> }
) {
  // If templates already exist, skip creation regardless of language.
  // This prevents duplicates when the user switches their UI language.
  const existing = templateList.getTemplateDocs();
  if (existing.length >= BUILT_IN_TEMPLATE_COUNT) {
    return;
  }

  const creators = [
    createSprintPlanningTemplate,
    createOneOnOneTemplate,
    createWeeklyStandupTemplate,
    createProjectTrackerTemplate,
    createRetrospectiveTemplate,
    createBugReportTemplate,
    createFeatureRequestTemplate,
    createTeamWikiTemplate,
    createDecisionLogTemplate,
    createDailyJournalTemplate,
  ];

  // Only create templates that are missing (count-based, not title-based)
  for (let i = existing.length; i < creators.length; i++) {
    await creators[i](docsService);
  }
}
